import { Checkbox, Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken, lighten } from '@material-ui/core/styles/colorManipulator';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import DeployIcon from '@material-ui/icons/CallMade';
import TeardownIcon from '@material-ui/icons/CallReceived';
import DeployAndTeardownIcon from '@material-ui/icons/Repeat';
import { Alert } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { observer } from 'mobx-react-lite';
import moment, { Moment } from 'moment-timezone';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi, { WorfklowExecutionOptions } from '../../api/WindsorApi';
import ButtonPanel from '../../common/ButtonPanel';
import LinkRouter from '../../common/LinkRouter';
import LoadingIndicator from '../../common/LoadingIndicator';
import MiddleDot from '../../common/MiddleDot';
import MobxContext from '../../mobx/MobxContext';
import CreateExecutionDeploymentActionsTable from './CreateExecutionDeploymentActionsTable';
import CreateExecutionTestActionsTable from './CreateExecutionTestActionsTable';
import ExecutionStatusSnackbar from './ExecutionStatusSnackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    pageHeader: {
      margin: '16px 0'
    },
    section: {
      marginBottom: 32,
      padding: 16,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
    },
    executionPanelSection: {
      marginBottom: 32,
      marginTop: 24
    },
    teardownConfirmationPanel: {
      padding: '0 16px',
      marginTop: 16
    },
    dateTimeWrapper: {
      display: 'flex',
      alignItems: 'flex-end'
    },
    dateInput: {
      marginRight: 16
    },
    schedulePanel: {
      backgroundColor: theme.palette.type === 'light' ? lighten(theme.palette.background.default, 0.03) : darken(theme.palette.background.default, 0.03),
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
      padding: 16,
      marginTop: 16
    },
    breadcrumbs: {
      marginBottom: 8
    },
    breadcrumbLabel: {
      fontWeight: 500
    },
    alert: {
      marginBottom: 16
    },
    buttonPanelContainer: {
      display: 'flex',
      margin: '0 -8px'
    },
    executionPlanIcon: {
      margin: '8px 0'
    },
    deploymentActionsPanelHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 16
    },
    executeAllActionsCheckbox: {
      padding: '0 8px 0 0'
    },
    formActions: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: 36
    }
  })
);

const EXECUTION_PLAN_MAPPING = {
  deploy: 'DEPLOY_WITHOUT_TEARDOWN',
  deployWithTeardown: 'DEPLOY_WITH_TEARDOWN',
  teardown: 'TEARDOWN_ALL'
}

const TIME_ZONES = ['Pacific/Samoa', 'America/Anchorage', 'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'Atlantic/Bermuda', 'America/Argentina/Buenos_Aires', 'Atlantic/South_Georgia', 'Atlantic/Azores', 'Greenwich', 'Europe/Berlin', 'Europe/Helsinki',
  'Europe/Moscow', 'Asia/Dubai', 'Asia/Calcutta', 'Asia/Yekaterinburg', 'Asia/Kashgar', 'Asia/Bangkok', 'Asia/Shanghai',
  'Australia/Adelaide', 'Asia/Seoul', 'Australia/Melbourne', 'Pacific/Guadalcanal', 'Pacific/Fiji', 'Pacific/Tongatapu'
];

const windsorApi = new WindsorApi();

function WorkflowExecutionPage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match, enqueueSnackbar, closeSnackbar, history } = props;
  const familyId = match.params.familyId;
  const workflowId = match.params.workflowId;

  const classes = useStyles({});
  const { executionStore } = useContext(MobxContext);

  const [workflow, setWorkflow] = useState(null);
  const [family, setFamily] = useState(null);

  const [executionPlan, setExecutionPlan] = useState(null);
  const [executionSchedule, setExecutionSchedule] = useState('immediate');
  const [deploymentActions, setDeploymentActions] = useState(null);
  const [testActions, setTestActions] = useState(null);
  const [numSelectedDeploymentActions, setNumSelectedDeploymentActions] = useState(null);
  const [numSelectedTestActions, setNumSelectedTestActions] = useState(null);
  const [verifyTeardown, setVerifyTeardown] = useState(false);
  const [executeAllDeploymentActions, setExecuteAllDeploymentActions] = useState(true);
  const [executeAllTestActions, setExecuteAllTestActions] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(moment.utc());
  const [scheduledTime, setScheduledTime] = useState(moment.utc().format('HH:mm'));
  const [selectedTimezone, setSelectedTimezone] = useState('Greenwich');
  const [comment, setComment] = useState('');

  useEffect(() => {
    Promise.all([
      windsorApi.getWorkflow(familyId, workflowId),
      windsorApi.getFamily(familyId),
    ]).then(([workflow, family]) => {
      setWorkflow(workflow);
      setFamily(family);
      setDeploymentActions(workflow.DeploymentRecipe.map(action => ({
        actionGroup: action.ActionGroup || 'default',
        actionName: action.ActionName,
        deploymentName: action.DeploymentName,
        deckName: action.DeckName,
        selected: true,
        force: false,
        continue: false
      })));
      setTestActions(workflow.TestFunctions.map(action => ({
        actionGroup: action.ActionGroup || 'default',
        actionName: action.ActionName,
        testName: action.TestName,
        testVersion: action.Version,
        selected: true
      })));
    });
  }, [familyId, workflowId]);

  useEffect(() => {
    if (deploymentActions) {
      setNumSelectedDeploymentActions(deploymentActions.reduce((acc, item) => {
        if (item.selected) {
          acc++;
        }
        return acc;
      }, 0));
    }
  }, [deploymentActions]);

  useEffect(() => {
    if (testActions) {
      setNumSelectedTestActions(testActions.reduce((acc, item) => {
        if (item.selected) {
          acc++;
        }
        return acc;
      }, 0));
    }
  }, [testActions]);

  function onExecutionPlanChanged(value: string) {
    setExecutionPlan(value);
    setVerifyTeardown(false);
  }

  function onExecutionScheduleChanged(event: React.ChangeEvent<{ name?: string; value: string }>) {
    setExecutionSchedule(event.target.value);
  }

  function onScheduledDateChange(date: Moment | null) {
    setScheduledDate(date);
  }

  function onScheduledTimeChange(event: React.ChangeEvent<{ value: string }>) {
    setScheduledTime(event.target.value);
  }

  function onSelectedTimezoneChanged(event: React.ChangeEvent<{ name?: string; value: string }>) {
    setSelectedTimezone(event.target.value);
  }

  function onCommentChanged(event: React.ChangeEvent<{ value: string }>) {
    setComment(event.target.value);
  }

  function onToggleVerifyTeardown(event: React.ChangeEvent<HTMLInputElement>) {
    setVerifyTeardown(event.target.checked);
  }

  const onDeploymentActionSelectionToggled = useCallback((action: any) => {
    setDeploymentActions(prevActions => {
      const deploymentActionsCopy = prevActions.slice();
      const index = deploymentActionsCopy.indexOf(action);
      if (index !== -1) {
        deploymentActionsCopy[index] = { ...action, selected: !action.selected };
      }
      return deploymentActionsCopy;
    });
  }, []);

  const onTestActionSelectionToggled = useCallback((action: any) => {
    setTestActions(prevActions => {
      const testActionsCopy = prevActions.slice();
      const index = testActionsCopy.indexOf(action);
      if (index !== -1) {
        testActionsCopy[index] = { ...action, selected: !action.selected };
      }
      return testActionsCopy;
    });
  }, []);

  const onContinueActionToggled = useCallback((action: any) => {
    setDeploymentActions(prevActions => {
      const deploymentActionsCopy = prevActions.slice();
      const index = deploymentActionsCopy.indexOf(action);
      if (index !== -1) {
        deploymentActionsCopy[index] = { ...action, continue: !action.continue };
      }
      return deploymentActionsCopy;
    });
  }, []);

  const onForceUpdateActionToggled = useCallback((action: any) => {
    setDeploymentActions(prevActions => {
      const deploymentActionsCopy = prevActions.slice();
      const index = deploymentActionsCopy.indexOf(action);
      if (index !== -1) {
        deploymentActionsCopy[index] = { ...action, force: !action.force };
      }
      return deploymentActionsCopy;
    });
  }, []);

  const onExecuteAllDeploymentActionsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.checked;
    setExecuteAllDeploymentActions(selected);
    setDeploymentActions(prevActions => prevActions.map(action => ({ ...action, selected })));
  };

  const onExecuteAllTestActionsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.checked;
    setExecuteAllTestActions(selected);
    setTestActions(prevActions => prevActions.map(action => ({ ...action, selected })));
  };

  const onSelectAllDeploymentActionsToggled = (checked: boolean) => {
    setDeploymentActions(deploymentActions.map(action => ({ ...action, selected: checked })));
  };

  const onSelectAllTestActionsToggled = (checked: boolean) => {
    setTestActions(testActions.map(action => ({ ...action, selected: checked })));
  };

  function onCancel() {
    history.goBack();
  }

  function onExecuteWorkflow(options: WorfklowExecutionOptions) {
    const promise = executionStore.executeWorkflow(familyId, workflowId, options);
    enqueueSnackbar('', {
      persist: true,
      content: (key) => (
        <ExecutionStatusSnackbar
          workflow={workflow}
          executionPromise={promise}
          onClose={() => closeSnackbar(key)}
        />
      )
    });
    history.push(`families/${familyId}/workflows/${workflowId}?tab=executions`);
  }

  function handleExecuteWorkflow() {
    if (!executionPlan ||
      ((executionPlan === 'teardown' || executionPlan === 'deployWithTeardown') && !verifyTeardown) ||
      (numSelectedDeploymentActions === 0 && numSelectedTestActions === 0)) {
      // Show validation error
      setShowErrors(true);
    } else {
      const actionOptions = [
        ...deploymentActions,
        ...(executionPlan !== 'teardown' ? testActions : [])
      ].filter(action => action.selected).reduce((accumulator, item) => {
        const actionName = item.actionName;
        accumulator.selected.push(actionName);
        if (item.force) {
          accumulator.force.push(actionName);
        }
        if (item.continue) {
          accumulator.continue.push(actionName);
        }
        return accumulator;
      }, {
        selected: [],
        force: [],
        continue: []
      });

      const executionOptions = {
        executionPlan: EXECUTION_PLAN_MAPPING[executionPlan],
        scheduledTime: executionSchedule === 'scheduled' ? moment.tz(`${scheduledDate.format('YYYY-MM-DD')} ${scheduledTime}`, selectedTimezone).tz("Greenwich").format('YYYY-MM-DD HH:mm:ss z') : undefined,
        delayed: executionSchedule === 'delayed',
        comment,
        selectedActions: !executeAllDeploymentActions || !executeAllTestActions ? actionOptions.selected : undefined,
        forcedUpdateActions: actionOptions.force.length ? actionOptions.force : undefined,
        continueActions: actionOptions.continue.length ? actionOptions.continue : undefined
      };

      onExecuteWorkflow(executionOptions);
    }
  }

  if (workflow && family && deploymentActions && testActions && numSelectedDeploymentActions !== null && numSelectedTestActions !== null) {
    return (
      <div className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
          <LinkRouter color="textSecondary" to={`/families`}>
            Families
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
            {family.Name}
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${familyId}`}>
            Workflows
          </LinkRouter>
          <LinkRouter color="textSecondary" to={`/families/${familyId}/workflows/${workflowId}`}>
            {workflow.Name}
          </LinkRouter>
          <Typography color="textPrimary">execute</Typography>
        </Breadcrumbs>
        <Divider />
        <div className={classes.pageHeader}>
          <Typography variant="h6" gutterBottom>Execute Workflow: {workflow.Name}</Typography>
          <div>
            <Typography className={classes.breadcrumbLabel} color="primary" display="inline" >
              Family: {family.Name}
            </Typography>
            <MiddleDot />
            <Typography className={classes.breadcrumbLabel} color="primary" display="inline" >
              Environment: {workflow.AccountName}
            </Typography>
            <MiddleDot />
            <Typography className={classes.breadcrumbLabel} color="primary" display="inline" >
              Region: {workflow.Region}
            </Typography>
            <MiddleDot />
            <Typography className={classes.breadcrumbLabel} color="primary" display="inline" >
              Last Updated: {moment.unix(parseInt(workflow.LastModified)).fromNow()}
            </Typography>
          </div>
        </div>
        <Divider light />
        <div className={classes.executionPanelSection}>
          <Typography gutterBottom color={showErrors && !executionPlan ? 'error' : 'textPrimary'}>Select an execution plan</Typography>
          {showErrors && !executionPlan && <Alert severity="warning" className={classes.alert}>
            <Typography>Execution plan is required</Typography>
          </Alert>}
          <div className={classes.buttonPanelContainer}>
            <ButtonPanel selected={executionPlan === 'deploy'} onClick={() => onExecutionPlanChanged('deploy')}>
              <Typography variant="subtitle1">Deploy</Typography>
              <DeployIcon className={classes.executionPlanIcon} />
              <Typography variant="caption" align="center">
                All selected actions will be deployed and any included tests will be executed
              </Typography>
            </ButtonPanel>
            <ButtonPanel selected={executionPlan === 'deployWithTeardown'} onClick={() => onExecutionPlanChanged('deployWithTeardown')}>
              <Typography variant="subtitle1">Deploy & Teardown</Typography>
              <DeployAndTeardownIcon className={classes.executionPlanIcon} />
              <Typography variant="caption" align="center">
                All selected actions will be deployed and torn down after executing any included tests
              </Typography>
            </ButtonPanel>
            <ButtonPanel selected={executionPlan === 'teardown'} onClick={() => onExecutionPlanChanged('teardown')}>
              <Typography variant="subtitle1">Teardown</Typography>
              <TeardownIcon className={classes.executionPlanIcon} />
              <Typography variant="caption" align="center">
                The deployments for all selected actions will be torn down
              </Typography>
            </ButtonPanel>
          </div>
          {(executionPlan === 'teardown' || executionPlan === 'deployWithTeardown') && <div className={classes.teardownConfirmationPanel}>
            <Typography color="error">Are you sure you want to teardown all existing Faro deployments in this workflow?</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={verifyTeardown}
                  onChange={onToggleVerifyTeardown}
                  value="verifyTeardown"
                  color="primary"
                />
              }
              label="Yes, teardown"
            />
            {showErrors && (executionPlan === 'teardown' || executionPlan === 'deployWithTeardown') && !verifyTeardown && <Typography color="error">
              Please verify that you want to teardown
              </Typography>}
          </div>}
        </div>
        <div className={classes.section}>
          <Typography gutterBottom>When would you like this workflow to be executed?</Typography>
          <Select
            value={executionSchedule}
            onChange={onExecutionScheduleChanged}
            fullWidth
          >
            <MenuItem value='immediate'>
              Execute immediately
                </MenuItem>
            <MenuItem value='delayed'>
              Aquire approvals for later execution
                </MenuItem>
            <MenuItem value='scheduled'>
              Schedule an execution time
                </MenuItem>
          </Select>
          {executionSchedule === 'scheduled' && <div className={classes.schedulePanel}>
            <Typography gutterBottom>Select a scheduled time</Typography>
            <div className={classes.dateTimeWrapper}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/DD/YYYY"
                margin="none"
                className={classes.dateInput}
                fullWidth
                value={scheduledDate}
                onChange={onScheduledDateChange}
                KeyboardButtonProps={{
                  'aria-label': 'scheduled date',
                }}
              />
              <TextField
                fullWidth
                type="time"
                value={scheduledTime}
                className={classes.dateInput}
                onChange={onScheduledTimeChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 60, // 1 min
                }}
              />
              <Select
                value={selectedTimezone}
                onChange={onSelectedTimezoneChanged}
                fullWidth
              >
                {TIME_ZONES.map(tz => <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>)}
              </Select>
            </div>
          </div>}
        </div>

        <div>
          {showErrors && numSelectedDeploymentActions === 0 && numSelectedTestActions === 0 && <Alert severity="warning" className={classes.alert}>
            <Typography>At least one action must be selected</Typography>
          </Alert>}
          {deploymentActions.length > 0 && <div className={classes.section}>
            <div className={classes.deploymentActionsPanelHeader}>
              <Typography>Deployment Actions</Typography>
              <Tooltip title="All deployment actions in the workflow will be executed">
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.executeAllActionsCheckbox}
                      checked={executeAllDeploymentActions}
                      onChange={onExecuteAllDeploymentActionsChanged}
                      value="executeAll"
                      color="default"
                    />
                  }
                  label="Execute all deploy actions"
                />
              </Tooltip>
            </div>
            {numSelectedDeploymentActions > 0 && !executeAllDeploymentActions && <Alert severity="info" className={classes.alert}>
              <Typography>{numSelectedDeploymentActions} deployment action{numSelectedDeploymentActions !== 1 ? 's' : ''} selected</Typography>
            </Alert>}
            <CreateExecutionDeploymentActionsTable
              enableSelection={!executeAllDeploymentActions}
              items={deploymentActions}
              numSelected={numSelectedDeploymentActions}
              onRowSelectionToggled={onDeploymentActionSelectionToggled}
              onContinueToggled={onContinueActionToggled}
              onForceUpdateToggled={onForceUpdateActionToggled}
              onSelectAllToggled={onSelectAllDeploymentActionsToggled}
            />
          </div>}
          {executionPlan !== 'teardown' && testActions.length > 0 && <div className={classes.section}>
            <div className={classes.deploymentActionsPanelHeader}>
              <Typography>Test Actions</Typography>
              <Tooltip title="All test actions in the workflow will be executed">
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.executeAllActionsCheckbox}
                      checked={executeAllTestActions}
                      onChange={onExecuteAllTestActionsChanged}
                      value="executeAll"
                      color="default"
                    />
                  }
                  label="Execute all test actions"
                />
              </Tooltip>
            </div>
            {numSelectedTestActions > 0 && !executeAllTestActions && <Alert severity="info" className={classes.alert}>
              <Typography>{numSelectedTestActions} test action{numSelectedTestActions !== 1 ? 's' : ''} selected</Typography>
            </Alert>}
            <CreateExecutionTestActionsTable
              enableSelection={!executeAllTestActions}
              items={testActions}
              numSelected={numSelectedTestActions}
              onRowSelectionToggled={onTestActionSelectionToggled}
              onSelectAllToggled={onSelectAllTestActionsToggled}
            />
          </div>}
          <div className={classes.section}>
            <Typography gutterBottom>Comment</Typography>
            <TextField
              multiline
              value={comment}
              rows="1"
              placeholder="Add an optional comment."
              margin="none"
              fullWidth
              onChange={onCommentChanged}
            />
          </div>
        </div>
        <div className={classes.formActions}>
          <Button onClick={onCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleExecuteWorkflow} color="primary" variant="contained">
            Execute Workflow
          </Button>
        </div>
      </div>
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }
};

export default withRouter(withSnackbar(observer(WorkflowExecutionPage)));
